/**
 * SEO/i18n Utilities - Centralized language and URL helpers
 * Single source of truth for all SEO-related constants and functions
 */

import { SEO_DOMAIN, SUPPORTED_LANGUAGES, PUBLIC_PAGE_ROUTES } from "./route-registry";

// Re-export for consumers that already import from seo.ts
export { SEO_DOMAIN };

// ============================================================================
// CONSTANTS
// ============================================================================

/** Set of all valid language codes */
export const validLanguages = new Set<string>(SUPPORTED_LANGUAGES);

/** Set of non-English language codes (used for /:lang routes) */
export const nonEnglishLanguages = new Set<string>(
  SUPPORTED_LANGUAGES.filter((code) => code !== "en")
);

/** RTL language codes */
export const rtlLanguages = new Set<string>(["ar"]);

/** OG Locale mapping for social sharing */
export const OG_LOCALE_MAP: Record<string, string> = {
  en: "en_US",
  es: "es_ES",
  fr: "fr_FR",
  de: "de_DE",
  it: "it_IT",
  pt: "pt_PT",
  nl: "nl_NL",
  sv: "sv_SE",
  fi: "fi_FI",
  el: "el_GR",
  ar: "ar_SA",
  tr: "tr_TR",
  zh: "zh_CN",
  ja: "ja_JP",
  ko: "ko_KR",
  id: "id_ID",
  hi: "hi_IN",
  pl: "pl_PL",
  th: "th_TH",
  vi: "vi_VN",
};

// ============================================================================
// PAGE KEY MAPPING
// ============================================================================

export type PageKey =
  | "home"
  | "governmentSolutions"
  | "technologyPlatform"
  | "operationalDelivery"
  | "globalPresence"
  | "tourismTrade"
  | "caseStudies"
  | "resources"
  | "about"
  | "contact"
  | "procurement"
  | "governance"
  | "implementation"
  | "dataPrivacy"
  | "security"
  | "faq"
  | "legalNotice"
  | "privacyPolicy"
  | "terms"
  | "notFound";

/** Maps URL path segments to page keys for i18n lookups */
export const pathToPageKey: Record<string, PageKey> = {
  "": "home",
  "government-solutions": "governmentSolutions",
  "technology-platform": "technologyPlatform",
  "operational-delivery": "operationalDelivery",
  "global-presence": "globalPresence",
  "tourism-trade-support": "tourismTrade",
  "case-studies": "caseStudies",
  resources: "resources",
  about: "about",
  contact: "contact",
  procurement: "procurement",
  governance: "governance",
  implementation: "implementation",
  "data-privacy": "dataPrivacy",
  security: "security",
  faq: "faq",
  "legal-notice": "legalNotice",
  "privacy-policy": "privacyPolicy",
  terms: "terms",
};

/** Page names for breadcrumb structured data */
export const PAGE_NAMES: Record<PageKey, string> = {
  home: "Home",
  governmentSolutions: "Government Solutions",
  technologyPlatform: "Technology Platform",
  operationalDelivery: "Operational Delivery",
  globalPresence: "Global Presence",
  tourismTrade: "Tourism & Trade Support",
  caseStudies: "Case Studies",
  resources: "Resources",
  about: "About",
  contact: "Contact",
  procurement: "Procurement",
  governance: "Governance",
  implementation: "Implementation",
  dataPrivacy: "Data Privacy",
  security: "Security",
  faq: "FAQ",
  legalNotice: "Legal Notice",
  privacyPolicy: "Privacy Policy",
  terms: "Terms of Use",
  notFound: "Page Not Found",
};

// ============================================================================
// CENTRALIZED ROUTE REGISTRY
// ============================================================================

/**
 * Single source of truth for all public routes (with pageKey mapping).
 * Derived from PUBLIC_PAGE_ROUTES in route-registry.ts.
 * Used by App.tsx routing, sitemap generation, and SEO metadata.
 */
export const PUBLIC_PAGES: Array<{
  path: string;
  pageKey: PageKey;
  priority: number;
  changefreq: string;
}> = PUBLIC_PAGE_ROUTES.map((route) => ({
  ...route,
  pageKey: pathToPageKey[route.path] || "home",
}));

// ============================================================================
// URL BUILDING FUNCTIONS
// ============================================================================

/**
 * Builds a canonical URL for the given language and page path.
 * English routes have no prefix; other languages get /{lang}/ prefix.
 */
export function buildCanonicalUrl(lang: string, pagePath: string): string {
  const cleanPath = pagePath ? `/${pagePath}` : "";
  if (lang === "en") {
    return `${SEO_DOMAIN}${cleanPath}`;
  }
  return `${SEO_DOMAIN}/${lang}${cleanPath}`;
}

/**
 * Builds a localized URL for a specific target language.
 * Alias for buildCanonicalUrl for clarity in hreflang generation.
 */
export function buildLocalizedUrl(targetLang: string, pagePath: string): string {
  return buildCanonicalUrl(targetLang, pagePath);
}

// ============================================================================
// PATH PARSING
// ============================================================================

export interface ParsedPath {
  lang: string;
  pagePath: string;
  pageKey: PageKey;
}

/**
 * Parses a pathname to extract language and page path.
 * Returns language code (defaulting to 'en' for English routes at root).
 */
export function parsePathname(pathname: string, fallbackLang: string = "en"): ParsedPath {
  const pathParts = pathname.split("/").filter(Boolean);

  let pagePath = "";
  let currentLang = "en";

  if (pathParts.length === 0) {
    // Root path = home
    pagePath = "";
    currentLang = fallbackLang || "en";
  } else if (validLanguages.has(pathParts[0]) && pathParts[0] !== "en") {
    // Non-English language prefix detected
    currentLang = pathParts[0];
    pagePath = pathParts.slice(1).join("/");
  } else {
    // English (no prefix) or direct page path
    currentLang = "en";
    pagePath = pathParts.join("/");
  }

  // Get the first segment for matching
  const firstSegment = pagePath.split("/")[0] || "";
  // Empty segment = home; known segment = mapped key; unknown = notFound (avoids reusing home metadata on 404s)
  const pageKey: PageKey =
    firstSegment === ""
      ? "home"
      : (pathToPageKey[firstSegment] ?? "notFound");

  return { lang: currentLang, pagePath, pageKey };
}

// ============================================================================
// DOM MANIPULATION HELPERS
// ============================================================================

/**
 * Updates or creates a meta tag with the given selector and attribute.
 */
export function updateMetaTag(
  selector: string,
  attribute: string,
  value: string
): void {
  const element = document.querySelector(selector);
  if (element) {
    element.setAttribute(attribute, value);
  }
}

/**
 * Updates or creates a link tag with the given rel and href.
 */
export function updateLinkTag(rel: string, href: string): void {
  let link = document.querySelector(
    `link[rel="${rel}"]`
  ) as HTMLLinkElement | null;
  if (link) {
    link.href = href;
  } else {
    link = document.createElement("link");
    link.rel = rel;
    link.href = href;
    document.head.appendChild(link);
  }
}

/**
 * Injects dynamic hreflang tags for all supported languages.
 * Removes existing hreflang links first to prevent duplicates.
 */
export function injectHreflangTags(pagePath: string): void {
  // Remove all existing hreflang links
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => {
    el.remove();
  });

  // Create hreflang for each language
  SUPPORTED_LANGUAGES.forEach((code) => {
    const link = document.createElement("link");
    link.rel = "alternate";
    link.hreflang = code;
    link.href = buildLocalizedUrl(code, pagePath);
    document.head.appendChild(link);
  });

  // Add x-default (points to English version)
  const xDefaultLink = document.createElement("link");
  xDefaultLink.rel = "alternate";
  xDefaultLink.hreflang = "x-default";
  xDefaultLink.href = buildLocalizedUrl("en", pagePath);
  document.head.appendChild(xDefaultLink);
}

/**
 * Updates document attributes for language and text direction.
 */
export function updateDocumentLang(lang: string): void {
  document.documentElement.lang = lang;
  document.documentElement.dir = rtlLanguages.has(lang) ? "rtl" : "ltr";
}
